import { useMemo, type ReactElement } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  VocabularyTermPicker,
  type VocabularyTermValue,
} from '@/components/vocabulary/VocabularyTermPicker';
import { usePatientMutations } from '@/features/patient-record/hooks/usePatientMutations';
import type {
  DemographicsViewModel,
  EthnicityTermViewModel,
  EthnicityViewModel,
} from '@/models/patient';

import { CollapsibleCard, type CollapsibleCardSummaryRow } from '../_shared/CollapsibleCard';

interface EthnicityCardProps {
  readonly guid: string;
  readonly demographics: DemographicsViewModel;
}

/**
 * Patient ethnicity — searchable HANCESTRO-backed multi-select with free-text
 * custom terms. Phase 1 surfaces only the `PATIENT` bucket; the BE schema also
 * carries `PATERNAL` and `MATERNAL` rows which we pass through unchanged.
 *
 * Picking a HANCESTRO match stores `{ termId, label }`; an unmatched query the
 * user accepts via the "Use … as custom term" footer stores `{ termId: null, label }`.
 */
export function EthnicityCard({ guid, demographics }: EthnicityCardProps): ReactElement {
  const { t } = useTranslation();
  const { updateDemographics } = usePatientMutations(guid);

  const patientRow = useMemo<EthnicityViewModel>(() => {
    const found = demographics.ethnicities.find((row) => row.type === 'PATIENT');
    return found ?? { id: null, type: 'PATIENT', terms: [] };
  }, [demographics.ethnicities]);

  const persist = (terms: readonly EthnicityTermViewModel[]): void => {
    const others = demographics.ethnicities.filter((row) => row.type !== 'PATIENT');
    const next: readonly EthnicityViewModel[] =
      terms.length === 0 ? others : [...others, { ...patientRow, terms }];
    updateDemographics.mutate({ ...demographics, ethnicities: next });
  };

  const addTerm = (value: VocabularyTermValue): void => {
    const termId = value.id.length > 0 ? value.id : null;
    const label = value.label.trim();
    if (label.length === 0) return;
    if (isDuplicate(patientRow.terms, { termId, label })) return;
    persist([...patientRow.terms, { termId, label }]);
  };

  const removeTerm = (index: number): void => {
    const next = patientRow.terms.filter((_, i) => i !== index);
    persist(next);
  };

  // Help the picker hide / dim already-attached terms so the user doesn't
  // double-add (e.g. Phase-1 doesn't merge duplicates server-side).
  const excludedIds = useMemo(
    () =>
      new Set(
        patientRow.terms
          .map((term) => term.termId)
          .filter((id): id is string => id !== null && id.length > 0),
      ),
    [patientRow.terms],
  );
  const excludedLabels = useMemo(
    () => new Set(patientRow.terms.map((term) => term.label.trim().toLowerCase())),
    [patientRow.terms],
  );

  const showEmpty = patientRow.terms.length === 0;

  const summaryRows: CollapsibleCardSummaryRow[] = [
    {
      label: t('patient.demographics.ethnicity'),
      value: patientRow.terms.length > 0 ? patientRow.terms.map((term) => term.label).join(', ') : '—',
      filled: patientRow.terms.length > 0,
    },
  ];

  return (
    <CollapsibleCard
      idPrefix="demographics"
      anchorId="ethnicity"
      title={t('patient.demographics.ethnicity')}
      count={patientRow.terms.length}
      summary={summaryRows}
    >
      {showEmpty && (
        <p className="text-sm text-muted-foreground italic mb-3">
          {t('patient.demographics.noEthnicity')}
        </p>
      )}
      {!showEmpty && (
        <ul className="flex flex-wrap gap-2 mb-3">
          {patientRow.terms.map((term, index) => (
            <li key={`${term.termId ?? 'custom'}-${term.label}-${String(index)}`}>
              <EthnicityChip
                term={term}
                onRemove={() => {
                  removeTerm(index);
                }}
              />
            </li>
          ))}
        </ul>
      )}
      <VocabularyTermPicker
        vocabularyId="hancestro"
        onChange={(next) => {
          if (next !== null) addTerm(next);
        }}
        placeholder={t('patient.demographics.ethnicityPlaceholder')}
        ariaLabel={t('patient.demographics.ethnicitySearchLabel')}
        allowCustom
        showInfo
        excludedIds={excludedIds}
        excludedLabels={excludedLabels}
      />
    </CollapsibleCard>
  );
}

function isDuplicate(
  terms: readonly EthnicityTermViewModel[],
  candidate: EthnicityTermViewModel,
): boolean {
  if (candidate.termId !== null) {
    if (terms.some((existing) => existing.termId === candidate.termId)) return true;
  }
  const normalized = candidate.label.trim().toLowerCase();
  return terms.some((existing) => existing.label.trim().toLowerCase() === normalized);
}

interface EthnicityChipProps {
  readonly term: EthnicityTermViewModel;
  readonly onRemove: () => void;
}

function EthnicityChip({ term, onRemove }: EthnicityChipProps): ReactElement {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 pl-3 pr-1 py-1 text-xs">
      <span className="font-medium text-foreground">{term.label}</span>
      {term.termId !== null && (
        <span className="font-mono text-[10px] text-muted-foreground">{term.termId}</span>
      )}
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-5 w-5"
        onClick={onRemove}
        aria-label={t('patient.demographics.removeEthnicity', { label: term.label })}
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </Button>
    </span>
  );
}
